/**
 * O que uma tela entrega à próxima na mesma navegação, para ela não reler o que já está na
 * memória.
 *
 * Dois usuários hoje, e os dois são troca de ROTA — dentro de um modal o problema não existe,
 * porque lá os dados vão por prop:
 *
 * - a ficha → a edição, que leu o item com `rel-domains`, a requisição mais cara da tela;
 * - a listagem ↔ o quadro, que são duas visões da mesma coleção e leem a mesma coisa.
 *
 * **Em memória, e não no `history.state`**: o estado do histórico é clonado estruturalmente, e
 * `Idea` e `Domain` perderiam os métodos no caminho.
 *
 * **Consome uma vez e some.** A rota precisa continuar funcionando sozinha — URL direta, F5,
 * voltar do navegador, Expandir vindo do modal —, então a entrega é um atalho, nunca um
 * requisito: quem não a encontra lê do servidor, como sempre. Guardar além de uma navegação
 * seria um cache, e cache tem invalidação; isto não tem o que invalidar porque não dura.
 *
 * **Um lugar só, com chave.** Vale uma navegação, e numa navegação há uma tela saindo e uma
 * entrando — não há duas entregas simultâneas para disputar o lugar. A chave é o que impede a
 * tela errada de consumir: quem pede diz para quem a entrega era.
 */
type Handover = { key: string, data: any }

let pending: Handover | null = null

/** Deixa os dados para quem chegar com esta chave. */
export function handOver(key: string, data: any): void {
    pending = {key, data}
}

/**
 * Pega o que foi deixado para ESTA chave, ou null.
 *
 * Limpa sempre, mesmo quando a chave não casa: o que sobrou é de uma navegação que não
 * aconteceu, e guardá-lo faria a entrega valer para uma viagem futura que ninguém pediu.
 */
export function takeHandover(key: string): any | null {
    const found = pending
    pending = null
    return found && found.key === key ? found.data : null
}
